import {
  useRef,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  Children,
  KeyboardEvent,
} from "react";
import classNames from "classnames";
import { ChevronLeftOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import { ChevronRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import styles from "./Carousel.module.scss";

export interface CarouselProps {
  children: ReactNode;
  small?: number;
  medium?: number;
  large?: number;
}

export function Carousel({ children, small, medium, large }: CarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const totalSlides = Children.count(children);

  const updateScrollButtons = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollButtons();

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateScrollButtons);
    return () => container.removeEventListener("scroll", updateScrollButtons);
  }, [updateScrollButtons]);

  const scrollToSlide = useCallback(
    (direction: "prev" | "next") => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const slidesToShow = Number(
        getComputedStyle(container).getPropertyValue("--slides-to-show") || 4,
      );
      const slideWidth = container.scrollWidth / totalSlides;
      const scrollAmount = slideWidth * slidesToShow;

      const targetScroll =
        direction === "next"
          ? container.scrollLeft + scrollAmount
          : container.scrollLeft - scrollAmount;

      container.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    },
    [totalSlides],
  );

  const handlePrevClick = () => {
    scrollToSlide("prev");
  };

  const handleNextClick = () => {
    scrollToSlide("next");
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && canScrollLeft) {
        event.preventDefault();
        scrollToSlide("prev");
      } else if (event.key === "ArrowRight" && canScrollRight) {
        event.preventDefault();
        scrollToSlide("next");
      } else if (event.key === "Home") {
        event.preventDefault();
        containerRef.current?.scrollTo({ left: 0, behavior: "smooth" });
      } else if (event.key === "End") {
        event.preventDefault();
        containerRef.current?.scrollTo({
          left: containerRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
    },
    [canScrollLeft, canScrollRight, scrollToSlide],
  );

  return (
    <div
      className={styles.carousel}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Carousel"
      aria-roledescription="carousel"
    >
      {canScrollLeft && (
        <button
          type="button"
          className={classNames(styles.button, styles["button--prev"])}
          onClick={handlePrevClick}
          aria-label="Previous"
          tabIndex={0}
        >
          <ChevronLeftOutlined size="medium" />
        </button>
      )}

      <div
        ref={containerRef}
        className={styles.container}
        style={
          {
            "--slides-to-show-small": small,
            "--slides-to-show-medium": medium,
            "--slides-to-show-large": large,
          } as React.CSSProperties
        }
        role="group"
      >
        <div className={styles.track}>
          {Children.map(children, (child, index) => (
            <div
              key={index}
              className={styles.slide}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${totalSlides}`}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {canScrollRight && (
        <button
          type="button"
          className={classNames(styles.button, styles["button--next"])}
          onClick={handleNextClick}
          aria-label="Next"
          tabIndex={0}
        >
          <ChevronRightOutlined size="medium" />
        </button>
      )}
    </div>
  );
}
